import { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Row, Col, Button, Tabs, Tab, Alert } from 'react-bootstrap';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateExecutionLog } from '../../features/execution-logs/api/executionLogsApi.js';
import { http as jobsHttp } from '../../features/jobs/api/http';
import TokenCorrectionPreview from './TokenCorrectionPreview';
import dayjs from 'dayjs';
import { MACHINES as MACHINE_NAMES } from '../../data/operatorConstants.js';

// Convert epoch ms to datetime-local string for input
const epochToLocal = (v) => {
  if (!v) return '';
  return dayjs(v).format('YYYY-MM-DDTHH:mm:ss');
};

// Convert datetime-local string back to epoch ms
const localToEpoch = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d.getTime();
};

const numOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return isNaN(n) ? null : n;
};

const PHASES = [
  'unwinding',
  'pre_stranding',
  'conductor_isolation',
  'rope',
  'interior_sheat',
  'armouring',
  'exterior_sheat',
  'coiling',
  'rewinding'
];

// Single source of truth — the six-machine list here used to omit dhc600 and
// ath600, so those two lines could never be filtered anywhere in the app.
const MACHINES = MACHINE_NAMES.map(m => m.toLowerCase());

const Field = ({ label, children, hint }) => (
  <Form.Group className="mb-3">
    <Form.Label className="mb-1 fs-9 fw-semibold text-700">{label}</Form.Label>
    {children}
    {hint && <Form.Text className="text-muted fs-10">{hint}</Form.Text>}
  </Form.Group>
);

const EditExecutionLogModal = ({ show, onHide, log }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({});
  const [saveError, setSaveError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  useEffect(() => {
    if (log) {
      setForm({
        // Machine
        machineId: log.machineId || '',
        shift: log.shift || '',

        // Job
        jobId: log.jobId || '',
        jobCode: log.jobCode || '',
        productLabel: log.productLabel || '',
        standardId: log.standardId || '',

        // Task
        taskId: log.taskId || '',
        phase: log.phase || '',
        taskPlannedLength: log.taskPlannedLength ?? '',
        taskExecutedLength: log.taskExecutedLength ?? '',
        taskCompleted: log.taskCompleted ?? false,

        // Token
        tokenId: log.tokenId || '',
        plannedLength: log.plannedLength ?? '',
        producedLength: log.producedLength ?? '',
        color: log.color || '',
        drum_material: log.drum_material || '',
        drum_diameter: log.drum_diameter ?? '',
        deliveryMode: log.deliveryMode || '',

        // Timing
        startedAt: epochToLocal(log.startedAt),
        completedAt: epochToLocal(log.completedAt),
        durationSec: log.durationSec ?? '',

        // Completion
        completedReason: log.completedReason || '',
        carry: log.carry ?? 0,

        // Orders
        orderIds: (log.orderIds || []).join(', '),
      });
      setSaveError(null);
      setPreview(null);
      setPreviewError(null);
    }
  }, [log]);

  const set = (key) => (e) => {
    const val = e?.target?.type === 'checkbox' ? e.target.checked : (e?.target?.value ?? e);
    // A preview belongs to one length. Keeping it on screen after the number
    // moves would show changes that are no longer the ones about to be made.
    if (key === 'producedLength') {
      setPreview(null);
      setPreviewError(null);
    }
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  // The produced length is not an attribute of this record — it is also the
  // task's executed length, the parent drum's consumption, the job material
  // summary the dashboards read, and the ProductionMetric row Operator
  // Performance aggregates. Writing it here alone is what left drum K-435695d8
  // reading 3 605 m in Mongo while everything else still said 13 961.
  //
  // So the length goes through the correction endpoint, which re-derives all of
  // them, and only the rest of the fields are saved as a plain record edit.
  const originalProduced = Number(log?.producedLength ?? 0);
  const nextProduced = numOrNull(form.producedLength);
  const lengthChanged =
    nextProduced !== null && Number(nextProduced) !== originalProduced;
  const canCascade = !!(log?.jobId && log?.tokenId);

  // The preview is the endpoint's own dry run — the same code path that does the
  // writing, asked not to write — so what is shown cannot drift from what happens.
  const runPreview = useCallback(async () => {
    setPreviewBusy(true);
    setPreviewError(null);
    try {
      const { data } = await jobsHttp.post(
        `/api/redis/jobs/${log.jobId}/tokens/${log.tokenId}/correct-length`,
        {
          producedLength: Number(nextProduced),
          reason: 'edited from the execution log',
          dryRun: true
        }
      );
      setPreview(data);
    } catch (err) {
      setPreviewError(err?.response?.data?.error || err.message || 'Request failed');
    } finally {
      setPreviewBusy(false);
    }
  }, [log?.jobId, log?.tokenId, nextProduced]);

  // Shown without being asked for. Hiding the consequences of an edit behind a
  // button means the one person who does not press it is the one who needed it.
  // Debounced so it fires on the number the user settled on, not on every digit
  // typed on the way there; the call writes nothing, so an extra one costs only
  // a round trip.
  useEffect(() => {
    if (!show || !lengthChanged || !canCascade) return undefined;
    const timer = setTimeout(runPreview, 600);
    return () => clearTimeout(timer);
  }, [show, lengthChanged, canCascade, runPreview]);

  // The drum's own specification, as opposed to the history of making it. These
  // five have to reach the JOB — the planner reads `plannedLength` off the job,
  // not off this row — and until 2026-10-12 none of them did: only
  // `producedLength` cascaded, so correcting a planned length from 4 120 to
  // 4 200 changed the record, left the product alone, and plan-next-phase went
  // on offering the old figure. See redis_dev/helpers/drumFields.js.
  const DRUM_FIELDS = [
    'plannedLength',
    'drum_material',
    'drum_diameter',
    'deliveryMode',
    'carry'
  ];

  const mutation = useMutation({
    mutationFn: async ({ body, correctTo }) => {
      let cascade = null;
      let drumCascade = null;

      // The drum's specification, into the job. Before the record edit: if this
      // is refused there is no point writing a row that disagrees with the job.
      if (canCascade) {
        const fields = {};
        for (const f of DRUM_FIELDS) {
          const to = body[f] ?? null;
          const from = log?.[f] ?? null;
          if (JSON.stringify(from) !== JSON.stringify(to) && to !== null) fields[f] = to;
        }
        if (Object.keys(fields).length) {
          const { data } = await jobsHttp.post(
            `/api/redis/jobs/${log.jobId}/tokens/${log.tokenId}/edit-drum`,
            { ...fields, reason: 'edited from the execution log' }
          );
          drumCascade = data;
        }
      }

      if (correctTo !== null) {
        const { data } = await jobsHttp.post(
          `/api/redis/jobs/${log.jobId}/tokens/${log.tokenId}/correct-length`,
          { producedLength: correctTo, reason: 'edited from the execution log' }
        );
        cascade = data;
        // The correction owns these two once it has actually written them.
        // On a no-op — the job already held this length, only the record had
        // drifted — the plain edit must still set them, or the record stays out
        // of step with the job it belongs to.
        if (data?.applied) {
          delete body.producedLength;
          delete body.taskExecutedLength;
        }
      }

      await updateExecutionLog(log._id, body);
      return cascade || drumCascade;
    },
    onSuccess: (cascade) => {
      queryClient.invalidateQueries({ queryKey: ['execution-logs'] });
      if (cascade) {
        queryClient.invalidateQueries({ queryKey: ['jobs'] });
        queryClient.invalidateQueries({ queryKey: ['operator-dashboard'] });
        queryClient.invalidateQueries({ queryKey: ['production-dashboard'] });
      }
      onHide();
    },
    onError: (err) => {
      setSaveError(err?.response?.data?.error || err.message || 'Save failed');
    }
  });

  const handleSave = () => {
    setSaveError(null);

    const body = {
      // Machine
      machineId: form.machineId,
      shift: form.shift || null,

      // Job — `jobId` is deliberately NOT sent. Who a production record belongs
      // to is re-attribution, not a correction: the server refuses it, and the
      // operation that does it properly is Move to another product
      // (reassign-drum), which previews it and records it on both products.
      jobCode: form.jobCode || null,
      productLabel: form.productLabel || null,
      standardId: form.standardId || null,

      // Task — `taskId` not sent, same reason as `jobId`.
      phase: form.phase,
      taskPlannedLength: numOrNull(form.taskPlannedLength),
      taskExecutedLength: numOrNull(form.taskExecutedLength),
      taskCompleted: form.taskCompleted,

      // Token — `tokenId` not sent, same reason.
      plannedLength: numOrNull(form.plannedLength),
      producedLength: numOrNull(form.producedLength),
      color: form.color || null,
      drum_material: form.drum_material || null,
      drum_diameter: numOrNull(form.drum_diameter),
      deliveryMode: form.deliveryMode || null,

      // Timing
      startedAt: localToEpoch(form.startedAt),
      completedAt: localToEpoch(form.completedAt),
      durationSec: numOrNull(form.durationSec),

      // Completion
      completedReason: form.completedReason || null,
      carry: numOrNull(form.carry) ?? 0,

      // Orders
      orderIds: form.orderIds
        ? form.orderIds.split(',').map((s) => s.trim()).filter(Boolean)
        : [],
    };

    mutation.mutate({
      body,
      correctTo: lengthChanged && canCascade ? Number(nextProduced) : null
    });
  };

  if (!log) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="fs-7">
          Edit Execution Log
          <span className="text-body-tertiary fs-9 ms-2">
            {log.tokenId} &mdash; {log.jobCode || log.jobId?.slice(0, 8)}
          </span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {saveError && (
          <Alert variant="danger" dismissible onClose={() => setSaveError(null)}>
            {saveError}
          </Alert>
        )}

        {lengthChanged && canCascade && (
          <Alert variant="info" className="fs-9 py-2">
            <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
              <div>
                Produced length {originalProduced.toLocaleString('ro-RO')} m &rarr;{' '}
                <strong>{Number(nextProduced).toLocaleString('ro-RO')} m</strong>. Saving also
                re-derives the task, the parent drum&apos;s consumption, the job material summary
                and the operator totals &mdash; not just this record.
              </div>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={runPreview}
                disabled={previewBusy}
                className="text-nowrap"
              >
                {previewBusy ? 'Checking…' : preview ? 'Refresh preview' : 'Preview changes'}
              </Button>
            </div>

            {previewError && (
              <div className="text-danger mt-2">Preview failed: {previewError}</div>
            )}
          </Alert>
        )}

        {lengthChanged && canCascade && preview && (
          <div className="mb-3">
            <TokenCorrectionPreview preview={preview} />
          </div>
        )}

        {lengthChanged && !canCascade && (
          <Alert variant="warning" className="fs-9 py-2">
            This record has no job or token id, so only the record itself can be changed. The task
            and material totals will not follow.
          </Alert>
        )}

        <Tabs defaultActiveKey="token" className="mb-3 fs-9">
          {/* ── Token tab (most important) ── */}
          <Tab eventKey="token" title="Token">
            <Row>
              <Col md={6}>
                <Field label="Token ID">
                  <Form.Control size="sm" value={form.tokenId} onChange={set('tokenId')} />
                </Field>
              </Col>
              <Col md={6}>
                <Field label="Color">
                  <Form.Control size="sm" value={form.color} onChange={set('color')} />
                </Field>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Field label="Planned Length (m)">
                  <Form.Control
                    size="sm"
                    type="number"
                    value={form.plannedLength}
                    onChange={set('plannedLength')}
                  />
                </Field>
              </Col>
              <Col md={6}>
                <Field label="Produced Length (m)">
                  <Form.Control
                    size="sm"
                    type="number"
                    value={form.producedLength}
                    onChange={set('producedLength')}
                  />
                </Field>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Field label="Started At" hint="Token production start time">
                  <Form.Control
                    size="sm"
                    type="datetime-local"
                    step="1"
                    value={form.startedAt}
                    onChange={set('startedAt')}
                  />
                </Field>
              </Col>
              <Col md={6}>
                <Field label="Completed At" hint="Token completion time">
                  <Form.Control
                    size="sm"
                    type="datetime-local"
                    step="1"
                    value={form.completedAt}
                    onChange={set('completedAt')}
                  />
                </Field>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Field label="Duration (sec)">
                  <Form.Control
                    size="sm"
                    type="number"
                    value={form.durationSec}
                    onChange={set('durationSec')}
                  />
                </Field>
              </Col>
              <Col md={4}>
                <Field label="Drum Material">
                  <Form.Select size="sm" value={form.drum_material} onChange={set('drum_material')}>
                    <option value="">-</option>
                    <option value="WOOD">WOOD</option>
                    <option value="METAL">METAL</option>
                  </Form.Select>
                </Field>
              </Col>
              <Col md={4}>
                <Field label="Drum Diameter">
                  <Form.Control
                    size="sm"
                    type="number"
                    value={form.drum_diameter}
                    onChange={set('drum_diameter')}
                  />
                </Field>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Field label="Delivery Mode">
                  <Form.Control size="sm" value={form.deliveryMode} onChange={set('deliveryMode')} />
                </Field>
              </Col>
              <Col md={6}>
                <Field label="Carry (m)">
                  <Form.Control
                    size="sm"
                    type="number"
                    value={form.carry}
                    onChange={set('carry')}
                  />
                </Field>
              </Col>
            </Row>
          </Tab>

          {/* ── Task tab ── */}
          <Tab eventKey="task" title="Task">
            <Row>
              <Col md={6}>
                <Field label="Task ID">
                  <Form.Control size="sm" value={form.taskId} onChange={set('taskId')} />
                </Field>
              </Col>
              <Col md={6}>
                <Field label="Phase">
                  <Form.Select size="sm" value={form.phase} onChange={set('phase')}>
                    <option value="">-</option>
                    {PHASES.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </Form.Select>
                </Field>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Field label="Task Planned Length (m)">
                  <Form.Control
                    size="sm"
                    type="number"
                    value={form.taskPlannedLength}
                    onChange={set('taskPlannedLength')}
                  />
                </Field>
              </Col>
              <Col md={4}>
                <Field label="Task Executed Length (m)">
                  <Form.Control
                    size="sm"
                    type="number"
                    value={form.taskExecutedLength}
                    onChange={set('taskExecutedLength')}
                  />
                </Field>
              </Col>
              <Col md={4}>
                <Field label="Task Completed">
                  <Form.Check
                    type="switch"
                    checked={form.taskCompleted}
                    onChange={set('taskCompleted')}
                    label={form.taskCompleted ? 'Yes' : 'No'}
                  />
                </Field>
              </Col>
            </Row>
          </Tab>

          {/* ── Job tab ── */}
          <Tab eventKey="job" title="Job">
            <Row>
              <Col md={6}>
                <Field label="Job ID">
                  <Form.Control size="sm" value={form.jobId} onChange={set('jobId')} />
                </Field>
              </Col>
              <Col md={6}>
                <Field label="Job Code">
                  <Form.Control size="sm" value={form.jobCode} onChange={set('jobCode')} />
                </Field>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Field label="Product Label">
                  <Form.Control size="sm" value={form.productLabel} onChange={set('productLabel')} />
                </Field>
              </Col>
              <Col md={6}>
                <Field label="Standard ID">
                  <Form.Control size="sm" value={form.standardId} onChange={set('standardId')} />
                </Field>
              </Col>
            </Row>
          </Tab>

          {/* ── Machine tab ── */}
          <Tab eventKey="machine" title="Machine">
            <Row>
              <Col md={6}>
                <Field label="Machine">
                  <Form.Select size="sm" value={form.machineId} onChange={set('machineId')}>
                    <option value="">-</option>
                    {MACHINES.map((m) => (
                      <option key={m} value={m}>{m.toUpperCase()}</option>
                    ))}
                  </Form.Select>
                </Field>
              </Col>
              <Col md={6}>
                <Field label="Shift">
                  <Form.Select size="sm" value={form.shift} onChange={set('shift')}>
                    <option value="">-</option>
                    <option value="A">A</option>
                    <option value="B">B</option>
                    <option value="C">C</option>
                  </Form.Select>
                </Field>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Field label="Completed Reason">
                  <Form.Control size="sm" value={form.completedReason} onChange={set('completedReason')} />
                </Field>
              </Col>
              <Col md={6}>
                <Field label="Order IDs" hint="Comma-separated">
                  <Form.Control size="sm" value={form.orderIds} onChange={set('orderIds')} />
                </Field>
              </Col>
            </Row>
          </Tab>
        </Tabs>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="link" className="text-600" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditExecutionLogModal;
